/**
 * The M7 rail's composer: one floating glass surface over the conversation.
 *
 * Files and link previews on top, then the field; attach, the model and
 * effort, and send along the bottom. The field grows a line at a time up to a
 * cap, then scrolls and offers Expand, which opens it to the whole rail. Send
 * fills with the theme accent only when there is something to send, and
 * becomes Stop while the agent is working. The model menu grows from its chip.
 */
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type RefObject,
} from "react";
import {
  ArrowUp,
  Loader2,
  Maximize2,
  Mic,
  Minimize2,
  Plus,
  Square,
  X,
} from "lucide-react";
import { KitFocusable, KitTooltip, KitTooltipTrigger } from "@/app/routes/dashboard/components/kit/Tooltip";
import type { ChatModel, ReasoningEffort } from "@/types/chat.type";
import {
  ATTACHMENT_ACCEPT,
  type ChatAttachmentItem,
} from "@/app/routes/dashboard/api/useSessionAttachments";
import { ComposerFiles, LinkCard } from "./RailMedia";
import { linksIn } from "./railLinks";
import { useModelPicker } from "./ModelPicker";
import { VoiceRecordingBar } from "./RailVoice";

/** The field grows to this many lines (fewer on a phone), or 40% of the rail if that is less. */
const CAP_LINES = 8;
const CAP_LINES_PHONE = 6;
const CAP_SHARE = 0.4;
const PHONE = "(max-width: 1023px)";
/** A link previews once you stop typing it, not letter by letter. */
const LINK_SETTLE_MS = 450;

/**
 * Talk, then check the words (Figma 05 · Voice). idle: the mic sits by Send.
 * recording: the composer becomes the recorder. writing: the note uploads and
 * is written down. checking: the words are in the field for you to fix.
 */
export type ComposerVoice = {
  phase: "idle" | "recording" | "writing" | "checking";
  levels: number[];
  elapsedMs: number;
  onStart: () => void;
  onStop: () => void;
  onCancel: () => void;
};

export type RailComposerProps = {
  value: string;
  onChange: (value: string) => void;
  /** Called with where the words sat, so they can fly into the conversation. */
  onSubmit: (from: { left: number; top: number } | null) => void;
  placeholder: string;
  inputLabel: string;
  disabled: boolean;
  isStreaming: boolean;
  onStop: () => void;
  isStopping: boolean;
  models: ChatModel[];
  selectedModel: ChatModel | null;
  onModelChange: (model: ChatModel) => void;
  selectedReasoningEffort: ReasoningEffort;
  onReasoningEffortChange: (effort: ReasoningEffort) => void;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  onAttachFiles: (files: File[]) => void;
  attachDisabled: boolean;
  /** Files attached since your last message: they go out with the next one. */
  attachments: ChatAttachmentItem[];
  onRemoveAttachment?: (documentId: string) => void;
  onRetryAttachment?: (documentId: string) => void;
  /** A message is held until an attached file finishes reading. */
  queued: boolean;
  /** The phone sheet opens to full height while you type. */
  onFieldFocus?: () => void;
  /** A direction pinned to one thing on the page: the quote chip over the field. */
  replyTo?: { label: string; quote: string; onClear: () => void };
  /**
   * What the message carries from the page, as an editor's chat does: the open
   * slide, and words highlighted on it. Each one clears with its X.
   */
  context?: { key: string; label: string; quote: string; clearLabel: string; onClear: () => void }[];
  /** The mic, where you may record (Owner and Contributor, a browser that can). */
  voice?: ComposerVoice;
};

/** The tallest the field grows before it scrolls. */
function fieldCap(el: HTMLTextAreaElement): number {
  const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 21;
  const lines = window.matchMedia(PHONE).matches ? CAP_LINES_PHONE : CAP_LINES;
  const panel = el.closest(".m7r")?.clientHeight ?? Infinity;
  return Math.max(lineHeight * 2, Math.min(lineHeight * lines, panel * CAP_SHARE));
}

export default function RailComposer({
  value,
  onChange,
  onSubmit,
  placeholder,
  inputLabel,
  disabled,
  isStreaming,
  onStop,
  isStopping,
  models,
  selectedModel,
  onModelChange,
  selectedReasoningEffort,
  onReasoningEffortChange,
  textareaRef,
  onAttachFiles,
  attachDisabled,
  attachments,
  onRemoveAttachment,
  onRetryAttachment,
  queued,
  onFieldFocus,
  replyTo,
  context,
  voice,
}: RailComposerProps) {
  const [expanded, setExpanded] = useState(false);
  // A draft emptied from outside (a send, a new chat) folds the field back down;
  // emptying it yourself, mid-edit, leaves it open.
  const [typed, setTyped] = useState(value);
  const [draftWas, setDraftWas] = useState(value);
  if (draftWas !== value) {
    setDraftWas(value);
    if (!value && expanded && typed !== value) setExpanded(false);
  }
  const formRef = useRef<HTMLFormElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const picker = useModelPicker({
    models,
    selectedModel,
    onModelChange,
    selectedReasoningEffort,
    onReasoningEffortChange,
    disabled,
  });

  const canStop = isStreaming;
  const voicePhase = voice?.phase ?? "idle";
  // Nothing goes out while a note is being written down: the words aren't in yet.
  const ready = value.trim() !== "" && !disabled && !isStreaming && voicePhase !== "writing";
  const shownPlaceholder = canStop ? "Working. Stop to interrupt." : placeholder;

  /* The field grows with the words up to its cap, then scrolls; past the cap
     the composer is marked so Expand shows. Written to the DOM, not to state:
     it changes on every keystroke. */
  useLayoutEffect(() => {
    const el = textareaRef.current;
    const form = formRef.current;
    if (!el || !form) return;
    const grow = () => {
      if (form.hasAttribute("data-expanded")) return;
      el.style.height = "auto";
      const needed = el.scrollHeight;
      const cap = fieldCap(el);
      const capped = needed > cap + 1;
      form.toggleAttribute("data-capped", capped);
      el.style.height = `${Math.min(needed, cap)}px`;
      el.style.overflowY = capped ? "auto" : "hidden";
    };
    grow();
    const observer = new ResizeObserver(grow);
    observer.observe(el.closest(".m7r") ?? el);
    return () => observer.disconnect();
  }, [value, shownPlaceholder, textareaRef, expanded]);

  /* Links preview once the typing settles. */
  const [linkSource, setLinkSource] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setLinkSource(value), LINK_SETTLE_MS);
    return () => window.clearTimeout(id);
  }, [value]);
  // Settled links that are still in the draft: a deleted one leaves at once.
  const links = useMemo(() => {
    if (!value.trim()) return [];
    const present = new Set(linksIn(value, 10).map((link) => link.url));
    return linksIn(linkSource).filter((link) => present.has(link.url));
  }, [linkSource, value]);

  const expand = () => {
    const form = formRef.current;
    const head = form?.parentElement?.querySelector<HTMLElement>(".m7r-head");
    if (form && head) {
      form.style.setProperty("--m7r-expand-top", `${head.offsetTop + head.offsetHeight + 8}px`);
    }
    const el = textareaRef.current;
    if (el) el.style.height = "";
    setExpanded(true);
    requestAnimationFrame(() => el?.focus({ preventScroll: true }));
  };
  const collapse = () => {
    setExpanded(false);
    requestAnimationFrame(() => textareaRef.current?.focus({ preventScroll: true }));
  };

  /* The glass floats over the conversation, so the conversation leaves it room. */
  useLayoutEffect(() => {
    const form = formRef.current;
    const log = form?.parentElement;
    if (!form || !log) return;
    const sync = () => log.style.setProperty("--m7r-composer-h", `${form.offsetHeight}px`);
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(form);
    return () => observer.disconnect();
  }, []);

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!ready) return;
    const field = textareaRef.current;
    const box = field?.getBoundingClientRect();
    setExpanded(false);
    onSubmit(box ? { left: box.left, top: box.top } : null);
  };

  const pending = attachments.filter((a) => a.state === "uploading" || a.state === "processing");

  return (
    <>
      <form
        ref={formRef}
        className="m7r-composer co-chrome-sheet"
        data-idle={disabled || isStreaming || undefined}
        data-expanded={expanded || undefined}
        data-recording={voicePhase === "recording" || undefined}
        onSubmit={submit}
        onKeyDown={(e) => {
          if (e.key !== "Escape" || !expanded) return;
          // Collapse the field, not the sheet around it, wherever focus is inside.
          e.preventDefault();
          e.stopPropagation();
          collapse();
        }}
      >
        {voicePhase === "recording" && voice ? (
          <VoiceRecordingBar
            levels={voice.levels}
            elapsedMs={voice.elapsedMs}
            onCancel={voice.onCancel}
            onStop={voice.onStop}
          />
        ) : (
          <>
          <ComposerFiles
            items={attachments}
            onRemove={onRemoveAttachment}
            onRetry={onRetryAttachment}
          />
          {links.length > 0 ? (
            <div className="m7r-tiles" aria-label="Links in your message">
              {links.map((link) => (
                <LinkCard key={link.url} link={link} />
              ))}
            </div>
          ) : null}
          {queued ? (
            <p className="m7r-queued" role="status">
              <Loader2 className="animate-spin" aria-hidden />
              Sends when {pending.length === 1 ? "your file is" : "your files are"} ready
            </p>
          ) : null}

          {replyTo ? (
            <div className="m7r-replying">
              <div className="m7r-replying-text">
                <span className="m7r-replying-label">{replyTo.label}</span>
                <span className="m7r-replying-quote">{replyTo.quote}</span>
              </div>
              <button
                type="button"
                className="m7r-replying-clear"
                aria-label={`Stop ${replyTo.label.charAt(0).toLowerCase()}${replyTo.label.slice(1)}`}
                title="Clear"
                onClick={() => {
                  replyTo.onClear();
                  textareaRef.current?.focus({ preventScroll: true });
                }}
              >
                <X aria-hidden strokeWidth={1.75} />
              </button>
            </div>
          ) : null}

          {!replyTo && context?.length
            ? context.map((item) => (
                <div key={item.key} className="m7r-replying" data-context={item.key}>
                  <div className="m7r-replying-text">
                    <span className="m7r-replying-label">{item.label}</span>
                    <span className="m7r-replying-quote">{item.quote}</span>
                  </div>
                  <button
                    type="button"
                    className="m7r-replying-clear"
                    aria-label={item.clearLabel}
                    title={item.clearLabel}
                    onClick={() => {
                      item.onClear();
                      textareaRef.current?.focus({ preventScroll: true });
                    }}
                  >
                    <X aria-hidden strokeWidth={1.75} />
                  </button>
                </div>
              ))
            : null}

          <label htmlFor="m7r-field" className="co-sr-only">
            {inputLabel}
          </label>
          <div className="m7r-field-wrap" data-waiting={(voicePhase === "writing" && !value.trim()) || undefined}>
            <textarea
              id="m7r-field"
              ref={textareaRef}
              rows={1}
              className="m7r-field co-input-own-focus"
              value={value}
              maxLength={8000}
              placeholder={shownPlaceholder}
              disabled={disabled}
              onFocus={onFieldFocus}
              onChange={(e) => {
                setTyped(e.target.value);
                onChange(e.target.value);
              }}
              onScroll={(e) =>
                e.currentTarget.toggleAttribute("data-scrolled", e.currentTarget.scrollTop > 2)
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  submit();
                }
              }}
            />
            <button
              type="button"
              className="m7r-expand"
              aria-label={expanded ? "Collapse the message field" : "Expand the message field"}
              title={expanded ? "Collapse" : "Expand"}
              onClick={expanded ? collapse : expand}
            >
              {expanded ? (
                <Minimize2 aria-hidden strokeWidth={1.75} />
              ) : (
                <Maximize2 aria-hidden strokeWidth={1.75} />
              )}
            </button>
          </div>

          {voicePhase === "writing" || voicePhase === "checking" ? (
            <div className="m7r-voice-words" aria-live="polite">
              {voicePhase === "writing" && !value.trim() ? <span className="m7r-voice-skeleton" aria-hidden /> : null}
              <span className="m7r-voice-hint">
                {voicePhase === "writing" ? "Writing it down" : "Check the words, then send"}
              </span>
            </div>
          ) : null}

          <div className="m7r-controls">
            <button
              type="button"
              className="m7r-attach"
              disabled={attachDisabled}
              aria-label="Attach a PDF or a video"
              title="Attach a PDF or a video"
              onClick={() => fileRef.current?.click()}
            >
              <Plus aria-hidden strokeWidth={1.75} />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept={ATTACHMENT_ACCEPT}
              multiple
              hidden
              onChange={(e) => {
                // Snapshot first: a live FileList empties once the value is reset.
                const files = e.target.files ? Array.from(e.target.files) : [];
                e.target.value = "";
                if (files.length) onAttachFiles(files);
              }}
            />
            {picker.chip}
            {voice && !canStop ? (
              <KitTooltipTrigger delay={400}>
                <KitFocusable>
                  <button
                    type="button"
                    className="m7r-mic"
                    aria-label="Talk, then check the words"
                    disabled={disabled || voicePhase === "writing"}
                    onClick={voice.onStart}
                  >
                    <Mic aria-hidden strokeWidth={1.75} />
                  </button>
                </KitFocusable>
                <KitTooltip placement="top">Talk, then check the words</KitTooltip>
              </KitTooltipTrigger>
            ) : null}
            {canStop ? (
              <button
                type="button"
                className="m7r-send"
                data-stop
                disabled={isStopping}
                aria-label={isStopping ? "Stopping the reply" : "Stop the reply"}
                title="Stop the reply"
                onClick={onStop}
              >
                {isStopping ? (
                  <Loader2 className="animate-spin" aria-hidden />
                ) : (
                  <Square aria-hidden fill="currentColor" strokeWidth={0} />
                )}
              </button>
            ) : (
              <button
                type="submit"
                className="m7r-send"
                data-ready={ready || undefined}
                aria-disabled={!ready}
                aria-label="Send"
                title="Send"
              >
                <ArrowUp aria-hidden strokeWidth={2} />
              </button>
            )}
          </div>
          </>
        )}
      </form>

      {/* A sibling of the glass composer, not inside it: nested glass cannot blur
          the conversation behind it, so the menu would read as see-through. */}
      {picker.menu}
    </>
  );
}
